import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Shield, Wifi, Monitor, Settings, FileText, Smartphone } from "lucide-react";

export interface ProfileTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  platforms: string[];
  payloads: string[];
}

const profileTemplates: ProfileTemplate[] = [
  {
    id: "security-baseline",
    name: "Security Baseline",
    description: "Essential security settings for all devices including passcode policies and restrictions.",
    icon: <Shield className="h-8 w-8 text-blue-600" />,
    category: "Security",
    platforms: ["macOS", "iOS", "iPadOS"],
    payloads: ["passcode", "restrictions", "firewall"]
  },
  {
    id: "corporate-wifi",
    name: "Corporate Wi-Fi",
    description: "Automatically connect devices to corporate Wi-Fi networks with proper security settings.",
    icon: <Wifi className="h-8 w-8 text-green-600" />,
    category: "Network",
    platforms: ["macOS", "iOS", "iPadOS", "tvOS"],
    payloads: ["wifi"]
  },
  {
    id: "device-management",
    name: "Device Management",
    description: "Comprehensive device management settings for corporate environments.",
    icon: <Monitor className="h-8 w-8 text-purple-600" />,
    category: "Management",
    platforms: ["macOS", "iOS", "iPadOS"],
    payloads: ["restrictions", "privacy-preferences"]
  },
  {
    id: "mobile-security",
    name: "Mobile Security",
    description: "Security configuration optimized for mobile devices and remote work.",
    icon: <Smartphone className="h-8 w-8 text-orange-600" />,
    category: "Security",
    platforms: ["iOS", "iPadOS"],
    payloads: ["passcode", "restrictions", "single-app-mode"]
  },
  {
    id: "custom-profile",
    name: "Custom Profile",
    description: "Start with a blank profile and configure payloads manually.",
    icon: <Settings className="h-8 w-8 text-gray-600" />,
    category: "Custom",
    platforms: ["macOS", "iOS", "iPadOS", "tvOS"],
    payloads: []
  }
];

interface ProfileTemplateSelectionProps {
  onTemplateSelect: (template: ProfileTemplate) => void;
}

export const ProfileTemplateSelection = ({ onTemplateSelect }: ProfileTemplateSelectionProps) => {
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const handleCancel = () => {
    navigate('/profiles');
  };

  return (
    <div className="h-full overflow-y-auto">
      {/* Compact Header */}
      <div className="border-b bg-background sticky top-0 z-10 px-4 lg:px-6 py-4">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleCancel}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Choose a Profile Template</h1>
            <p className="text-sm text-muted-foreground">
              Select from our collection of device management configurations
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 lg:px-6 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {profileTemplates.map((template) => (
            <GlassCard
              key={template.id}
              variant="default"
              hover={true}
              className={`cursor-pointer transition-all ${
                selectedTemplate === template.id
                  ? "ring-2 ring-primary/30 shadow-xl"
                  : ""
              }`}
              onClick={() => setSelectedTemplate(template.id)}
            >
              <GlassCardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-3">{template.icon}</div>
                    <GlassCardTitle className="text-lg">{template.name}</GlassCardTitle>
                    <GlassCardDescription className="text-sm mt-2">
                      {template.description}
                    </GlassCardDescription>
                  </div>
                </div>
              </GlassCardHeader>
              <GlassCardContent className="pt-0">
                <div className="space-y-3">
                  {/* Platforms */}
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Platforms
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {template.platforms.map((platform) => (
                        <span
                          key={platform}
                          className="px-2 py-1 bg-secondary text-secondary-foreground rounded text-xs"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action */}
                  <div className="pt-2 border-t">
                    <Button
                      variant={selectedTemplate === template.id ? "default" : "outline"}
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTemplateSelect(template);
                      }}
                    >
                      {selectedTemplate === template.id ? (
                        <>
                          Continue <ArrowRight className="h-4 w-4 ml-1" />
                        </>
                      ) : (
                        "Select"
                      )}
                    </Button>
                  </div>
                </div>
              </GlassCardContent>
            </GlassCard>
          ))}
        </div>
      </div>
    </div>
  );
};